'use client'

import { useEffect, useState } from 'react'
import { getSession, signIn } from 'next-auth/react'
import { toast } from '@/lib/toast'
import { useRouter, useSearchParams } from 'next/navigation'
import { useT } from '../i18n/LocaleProvider'
import { apiError } from '@/lib/i18n/api-error'
import { startAuth } from '@/lib/request'
import type { Session } from '@/lib/types'

import { RotateCcw } from 'lucide-react'
import Input from '../elements/Input'
import { Button } from '../elements/Button'
import { AuthDivider, AuthHeader, AuthShell } from './AuthShell'
import { AuthAside } from './AuthAside'
import { GoogleButton, OtpField, formatPhone, looksLikePhone, readContact, readLogin, signInErrorCode } from './AuthFields'
import type { Contact } from './AuthFields'

/**
 * Signing in, with one field for a phone number, an email address or a handle.
 *
 * A password by default, for any of the three. "Sign in with a code" swaps the password
 * for a code sent to whichever was typed: an email for an address, an SMS for a
 * number. A handle cannot take a code: there would be nowhere to send it
 * without saying which phone or email the handle belongs to. The code exchange is the same `otp` sign-in the sign-up wizard uses,
 * which also means a contact nobody has registered becomes a new account here,
 * and is sent on to finish it in the wizard.
 */
export default function Login() {
  const searchParams = useSearchParams()
  // Carried over from the sign-up code screen when the number turned out to
  // belong to an account already, so it arrives filled in.
  const [identity, setIdentity] = useState(() => searchParams.get('phone') ?? '')
  const [password, setPassword] = useState('')
  const [identityError, setIdentityError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [mode, setMode] = useState<'password' | 'code'>('password')

  /** Set once a code has gone out, which is what puts the code screen up. */
  const [sentTo, setSentTo] = useState<Contact | null>(null)
  const [code, setCode] = useState('')
  const [codeError, setCodeError] = useState('')
  const [resendLoading, setResendLoading] = useState(false)

  /** As in Register: the number is on an account it was never confirmed for. */
  const [numberTaken, setNumberTaken] = useState(false)

  const t = useT()
  const router = useRouter()
  const callbackUrl = searchParams.get('callbackUrl')
  const phoneMode = looksLikePhone(identity)

  useEffect(() => {
    if(searchParams.has('error')) {
      setLoading(false)
      setGoogleLoading(false)
      if(searchParams.get('error') == 'notFound') toast.error(t('auth.toast.accountNotFound'))
      if(searchParams.get('error') == 'serverError') toast.error(t('common.serverError'))
    }
  }, [searchParams])

  const handleIdentityChange = (event: any) => {
    setIdentity(event.target.value)
    setIdentityError('')
  }

  const handlePasswordChange = (event: any) => {
    setPassword(event.target.value)
    setPasswordError('')
  }

  const handleKeyDown = (event: any) => {
    if (event.key === 'Enter') handleSubmit(event)
  }

  const signInWithGoogle = async () => {
    setGoogleLoading(true)
    if(callbackUrl) return await signIn('google', { callbackUrl: callbackUrl })
    await signIn('google')
  }

  const withCallback = (path: string) => callbackUrl ? `${path}?callbackUrl=${callbackUrl}` : path

  const switchMode = () => {
    setMode(mode === 'password' ? 'code' : 'password')
    setIdentityError('')
    setPasswordError('')
  }

  const finish = () => {
    toast.success(t('auth.toast.loginSuccess'))
    setTimeout(() => {
      // Where they were headed before the sign-in wall, or the editor. Never
      // `/`: that redirects a session straight back here to `/profile`, and
      // the extra hop is visible as a flash of the home page.
      if(callbackUrl) router.push(callbackUrl)
      else router.push('/profile')
    }, 100)
  }

  /**
   * Reads the field for a code, or says under it what is wrong. Reported there
   * rather than as a toast: the message names which box is wrong.
   */
  const contactOrError = (): Contact | null => {
    if(!identity.trim()) {
      setIdentityError(t('auth.toast.identityEmpty'))
      return null
    }
    const contact = readContact(identity)
    if(!contact) setIdentityError(t(readLogin(identity)?.username ? 'auth.toast.codeNeedsContact' : 'auth.toast.identityInvalid'))
    return contact
  }

  const handleSubmit = (e: any) => {
    e.preventDefault()
    return mode === 'code' ? handleSendCode() : handleLogin()
  }

  const handleLogin = async () => {
    if(!identity.trim()) return setIdentityError(t('auth.toast.identityEmpty'))
    const login = readLogin(identity)
    if(!login) return setIdentityError(t('auth.toast.loginIdentityInvalid'))
    if(!password) return setPasswordError(t('auth.toast.passwordEmpty'))

    setLoading(true)
    const response = await signIn('credentials', {
      ...login,
      password,
      redirect: false
    })
    setLoading(false)
    if(!response) return toast.error(t('auth.toast.loginFailed'))
    if(response.error) {
      const code = signInErrorCode(response, 'auth.credentials_invalid')
      // An account with no password can only come in with a code, so the
      // screen goes there itself rather than leaving them to find the switch.
      if(code === 'auth.password_not_set') setMode('code')
      return toast.error(apiError(t, code, 'auth.toast.loginFailed'))
    }
    if(response.ok === false) return toast.error(t('auth.toast.loginFailed'))
    finish()
  }

  const handleSendCode = async () => {
    const contact = contactOrError()
    if(!contact) return

    setLoading(true)
    const res = await startAuth(contact)
    setLoading(false)
    if(!res) return toast.error(t('common.serverError'))
    if(res.error) return setIdentityError(apiError(t, res, 'auth.toast.loginFailed'))

    toast.success(t(contact.phone ? 'auth.toast.codeSentPhone' : 'auth.toast.codeSent'))
    setCode('')
    setCodeError('')
    setSentTo(contact)
  }

  const handleResend = async () => {
    if(!sentTo) return
    setResendLoading(true)
    const res = await startAuth(sentTo)
    setResendLoading(false)
    if(!res) return toast.error(t('common.serverError'))
    if(res.error) return toast.error(apiError(t, res, 'auth.toast.resendFailed'))
    setCode('')
    setCodeError('')
    toast.success(t(sentTo.phone ? 'auth.toast.resendSuccessPhone' : 'auth.toast.resendSuccess'))
  }

  const handleVerify = async (value: string) => {
    if(!sentTo) return
    if(value.length !== 6) return setCodeError(t('auth.toast.otpEmpty'))

    setLoading(true)
    setCodeError('')
    const response = await signIn('otp', { ...sentTo, code: value, redirect: false })

    if(!response) {
      setLoading(false)
      return toast.error(t('common.serverError'))
    }
    if(response.error) {
      setLoading(false)
      setCode('')
      const errorCode = signInErrorCode(response, 'verification.code_invalid')
      setNumberTaken(errorCode === 'auth.phone_unconfirmed')
      return setCodeError(apiError(t, errorCode, 'auth.toast.loginFailed'))
    }

    // A contact with no account behind it has just become one, and it still
    // owes a password, a name and a handle. The wizard picks up from the session.
    const session = await getSession() as Session | null
    const nextStep = session?.user?.nextStep
    setLoading(false)
    if(nextStep && nextStep !== 'done') return router.push(withCallback('/register'))
    finish()
  }

  const backToForm = () => {
    setSentTo(null)
    setCode('')
    setCodeError('')
    setNumberTaken(false)
  }

  const aside = <AuthAside sample />

  if(sentTo) {
    return (
      <AuthShell aside={aside}>
        <AuthHeader
          title={t('auth.otp.heading')}
          subtitle={sentTo.phone
            ? t('auth.otp.sentToPhone', { contact: formatPhone(sentTo.phone) })
            : t('auth.otp.sentTo', { contact: sentTo.email ?? '' })}
          backLabel={t(sentTo.phone ? 'auth.changeNumber' : 'auth.changeAddress')}
          onBack={backToForm}
        />

        <div className="flex flex-col gap-5">
          <OtpField value={code} onChange={setCode} onComplete={handleVerify} label={t('auth.otp.heading')} />
          { codeError && <p className="text-[13px] font-medium text-destructive">{codeError}</p> }
          {/* A password still gets into that account with this number, so the
              way forward is the password form, number kept. */}
          { numberTaken && (
            <Button variant="outline" width="full" onClick={() => { backToForm(); setMode('password') }}>
              {t('auth.otp.signInExisting')}
            </Button>
          ) }
          <div className="flex flex-col gap-2.5">
            <Button onClick={() => handleVerify(code)} isLoading={loading} width="full" loadingMessage={t('auth.verifying')}>
              {t('auth.verify')}
            </Button>
            <Button onClick={handleResend} isLoading={resendLoading} icon={RotateCcw} variant="ghost" width="full" loadingMessage={t('auth.sending')}>
              {t('auth.resend')}
            </Button>
          </div>
        </div>
      </AuthShell>
    )
  }

  // Where a code would go, said under the field once it is clear which.
  const codeHint = mode !== 'code' ? ''
    : phoneMode ? t('auth.smsHint')
    : readContact(identity)?.email ? t('auth.channelHint', { channel: t('auth.channelEmail') })
    : ''

  return (
    <AuthShell aside={aside}>
      <AuthHeader title={t('auth.login.heading')} subtitle={t('auth.login.subtitle')} />

      <div className="flex flex-col gap-5">
        <GoogleButton onClick={signInWithGoogle} isLoading={googleLoading} />

        <AuthDivider />

        <div className="flex flex-col gap-3.5">
          <Input
            value={identity}
            // A handle only signs in with a password, so the code form names two.
            label={t(mode === 'code' ? 'auth.identity' : 'auth.loginIdentity')}
            // Text rather than email: an email field refuses digits on some
            // phone keyboards and flags a number as malformed.
            type="text"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            placeholder={t(mode === 'code' ? 'auth.identityPlaceholder' : 'auth.loginIdentityPlaceholder')}
            onChange={handleIdentityChange}
            onKeyDown={handleKeyDown}
            description={codeHint}
            errorText={identityError}
            required
            autoFocus
            container="!max-w-none"
          />
          { mode === 'password' && (
            <>
              <Input
                value={password}
                label={t('auth.password')}
                type="password"
                autoComplete="current-password"
                placeholder={t('auth.passwordPlaceholder')}
                onChange={handlePasswordChange}
                onKeyDown={handleKeyDown}
                errorText={passwordError}
                required
                container="!max-w-none"
              />

              {/* Recovery is by email only. A number gets in with a code instead,
                  which the switch below already offers. */}
              { !phoneMode && (
                <button
                  type="button"
                  onClick={() => router.push(withCallback('/forgot'))}
                  className="cursor-pointer self-center text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground lg:self-end"
                >
                  {t('auth.login.forgotPassword')}
                </button>
              ) }
            </>
          ) }

          <Button
            type="button"
            onClick={handleSubmit}
            isLoading={loading}
            width="full"
            loadingMessage={t(mode === 'code' ? 'auth.sending' : 'auth.signingIn')}
          >
            {t(mode === 'code' ? 'auth.login.sendCode' : 'auth.login.submit')}
          </Button>

          <button
            type="button"
            onClick={switchMode}
            className="cursor-pointer self-center text-[13px] font-semibold text-foreground transition-colors hover:text-muted-foreground"
          >
            {t(mode === 'code' ? 'auth.login.usePassword' : 'auth.login.useCode')}
          </button>
        </div>

        <p className="text-center text-[13px] font-medium text-muted-foreground">
          {t('auth.login.newHere')}{' '}
          <button
            type="button"
            onClick={() => router.push(withCallback('/register'))}
            className="cursor-pointer font-semibold text-foreground transition-colors hover:text-muted-foreground"
          >
            {t('auth.login.createAccount')}
          </button>
        </p>
      </div>
    </AuthShell>
  )
}
